export const TARGET_YAW_ANGLE = 0.3;
export const GAUGE_MAX = TARGET_YAW_ANGLE * 2; // 0.6°

// "Re-cal" marks a check taken right after recalibration (yaw ball changed) —
// the best-case baseline, highlighted in violet across the fleet views
// (deliberately not green/amber/red = spec status, nor blue = "latest").
// Per-label traits (baseline flag, badge styling) live on the entry itself so
// YAW_LABELS stays the single source — no parallel maps keyed on the value.
export const YAW_LABELS = [
  { value: "post_adjustment", label: "Post-adj" },
  { value: "recheck", label: "Re-check" },
  {
    value: "recal",
    label: "Re-cal",
    baseline: true,
    badgeClass: "bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
  },
] as const;

export type YawCheckLabelValue = (typeof YAW_LABELS)[number]["value"];

interface YawLabelMeta {
  value: string;
  label: string;
  baseline?: boolean;
  badgeClass?: string;
}

function labelMeta(label?: string | null): YawLabelMeta | undefined {
  return label ? YAW_LABELS.find((l) => l.value === label) : undefined;
}

export const YAW_LABEL_DISPLAY: Record<string, string> = Object.fromEntries(
  YAW_LABELS.map((l) => [l.value, l.label]),
);

/** True for the re-cal baseline label (a check taken right after recalibration). */
export function isBaselineLabel(label?: string | null): boolean {
  return !!labelMeta(label)?.baseline;
}

/** Tailwind chip classes for a label, or null if it has no special styling. */
export function yawLabelBadgeClass(label?: string | null): string | null {
  return labelMeta(label)?.badgeClass ?? null;
}

/** History-table emphasis for the baseline (re-cal) row — same violet identity as the badge. */
export const BASELINE_ROW_CLASS = "bg-violet-50/40 dark:bg-violet-950/20";
export const BASELINE_ACCENT_CLASS = "border-l-2 border-l-violet-400";

/** Convert a displacement in mm to a yaw angle in degrees. */
export function computeYawAngle(mm: number): number {
  return (Math.atan(mm * 0.001) / 1.2) * (180 / Math.PI);
}

export interface SideMeasurements {
  fwd_static: number;
  fwd_max: number;
  fwd_min: number;
  aft_static: number;
  aft_max: number;
  aft_min: number;
}

export interface SideComputed {
  tot_static: number;
  tot_max: number;
  tot_min: number;
  fwd_yaw: number;
  aft_yaw: number;
  tot_yaw: number;
  /** YAW angle range = yaw(tot_max) - yaw(tot_min) */
  range: number;
  /** YAW angles for min/max of tot (used by gauge) */
  tot_min_yaw: number;
  tot_max_yaw: number;
}

export type YawStatus = "in-spec" | "high" | "critical";

export function computeYawStatus(staticYaw: number): YawStatus {
  if (staticYaw >= 0.15 && staticYaw <= 0.45) return "in-spec";
  if (staticYaw >= 0.05 && staticYaw <= 0.55) return "high";
  return "critical";
}

/** Map a yaw angle to 0–100% gauge position (0° = 0%, 0.6° = 100%). */
export function yawToPercent(value: number): number {
  return Math.max(0, Math.min(100, (value / GAUGE_MAX) * 100));
}

/** Extract PRT or STB side measurements from a yaw check response. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractSide(check: Record<string, any>, side: "prt" | "stb"): SideMeasurements {
  return {
    fwd_static: check[`${side}_fwd_static`] as number,
    fwd_max: check[`${side}_fwd_max`] as number,
    fwd_min: check[`${side}_fwd_min`] as number,
    aft_static: check[`${side}_aft_static`] as number,
    aft_max: check[`${side}_aft_max`] as number,
    aft_min: check[`${side}_aft_min`] as number,
  };
}

export function computeSide(m: SideMeasurements): SideComputed {
  const tot_static = m.fwd_static + m.aft_static;
  const tot_max = m.fwd_max + m.aft_max;
  const tot_min = m.fwd_min + m.aft_min;

  const fwd_yaw = computeYawAngle(m.fwd_static);
  const aft_yaw = computeYawAngle(m.aft_static);
  const tot_yaw = computeYawAngle(tot_static);
  const tot_min_yaw = computeYawAngle(tot_min);
  const tot_max_yaw = computeYawAngle(tot_max);
  const range = tot_max_yaw - tot_min_yaw;

  return { tot_static, tot_max, tot_min, fwd_yaw, aft_yaw, tot_yaw, range, tot_min_yaw, tot_max_yaw };
}
